/**
 * License data API service
 * Handles all license search and retrieval operations
 */
import api, { getErrorMessage } from './api'
import type {
  FCCLicense,
  FCCEntity,
  FCCLocation,
  FCCFrequency,
  ReferenceCopy,
  LicenseSearchParams,
  LicenseListResponse,
  SavedSearch,
  SavedSearchListResponse,
  WatchMatchesResponse,
  WatchSummaryResponse,
  FCCLicenseChangeLogListResponse,
  ChangeLogSortKey,
  ChangeLogBulkReviewRequest,
  BulkResult,
  ConstructionDeadlineListResponse,
  ConstructionBulkAcknowledgeRequest,
  ContactFilter,
  ContactMethod,
  ConstructionAlertState,
  ConstructionWorksheet,
  ConstructionFilingResponse,
  LicenseFacets,
} from '../types'

/**
 * The CRM/FCC facts that travel beside a license row: Do Not Contact, lead, client,
 * do-not-renew and pending-renewal status.
 *
 * Deliberately the same field names the search response uses, so both feed the same
 * reducer and a badge cannot mean one thing after a search and another after a refresh.
 * Adding a flag later (an "expiring soon", say) is a field here and a line there.
 */
export interface LicenseAnnotations {
  do_not_renew_usis: number[]
  lead_usis: number[]
  lead_usi_to_id: Record<string, number>
  client_usis: number[]
  client_usi_to_id: Record<string, number>
  dnc_call_signs: string[]
  pending_renewal_call_signs: string[]
}

/**
 * Re-read the badges for licenses we already have on screen.
 *
 * The search page caches its rows in sessionStorage so Back — and the Search menu item
 * — restore them instantly. The cache holds rows, not badges, so a restored page used
 * to show none: a license marked Do Not Contact a moment earlier looked un-flagged, and
 * one already added as a lead looked un-added.
 *
 * Caching the badges instead would be the wrong fix, because stale is exactly what they
 * were. The page paints the cached rows and then calls this for live values.
 *
 * POST because a restored page can carry hundreds of ids, and a URL that silently
 * truncates would annotate fewer rows than were asked about — the same failure, moved.
 */
export async function getLicenseAnnotations(
  usis: number[]
): Promise<LicenseAnnotations> {
  try {
    const response = await api.post<LicenseAnnotations>('/v1/licenses/annotations', { usis })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/**
 * Search for licenses with filters and pagination
 */
export async function searchLicenses(
  params: LicenseSearchParams
): Promise<LicenseListResponse> {
  try {
    const response = await api.get<LicenseListResponse>('/v1/licenses/search', { params })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/**
 * The option lists for the search filters — distinct states, radio services and
 * license statuses actually present in the imported FCC data, with counts.
 *
 * Cached server-side (recomputed when an import completes), so this is cheap to
 * call on page load.
 */
export async function getLicenseFacets(): Promise<LicenseFacets> {
  try {
    const response = await api.get<LicenseFacets>('/v1/licenses/facets')
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/**
 * Get detailed license information by call sign
 */
export async function getLicenseByCallSign(callSign: string): Promise<FCCLicense> {
  try {
    const response = await api.get<FCCLicense>(`/v1/licenses/${callSign}`)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/**
 * Get entity details for a license
 */
export async function getEntityByCallSign(callSign: string): Promise<FCCEntity> {
  try {
    const response = await api.get<FCCEntity>(`/v1/licenses/${callSign}/entity`)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/**
 * Get all locations for a license
 */
export async function getLocationsByCallSign(callSign: string): Promise<FCCLocation[]> {
  try {
    const response = await api.get<FCCLocation[]>(`/v1/licenses/${callSign}/locations`)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/**
 * Get all entities (licensee, contact, owner) for a license
 */
export async function getEntitiesByCallSign(callSign: string): Promise<FCCEntity[]> {
  try {
    const response = await api.get<FCCEntity[]>(`/v1/licenses/${callSign}/entities`)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/**
 * Get all frequency records for a license
 */
export async function getFrequenciesByCallSign(callSign: string): Promise<FCCFrequency[]> {
  try {
    const response = await api.get<FCCFrequency[]>(`/v1/licenses/${callSign}/frequencies`)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/**
 * Get the composed FCC "Reference Copy" payload for a call sign
 */
export async function getReferenceCopy(callSign: string): Promise<ReferenceCopy> {
  try {
    const response = await api.get<ReferenceCopy>(`/v1/licenses/${callSign}/reference`)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/**
 * Export search results to CSV (triggers browser download)
 */
export async function exportLicensesToCSV(params: LicenseSearchParams): Promise<Blob> {
  try {
    const response = await api.get('/v1/licenses/export/csv', {
      params,
      responseType: 'blob',
    })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

// ── Saved Searches ────────────────────────────────────────────────────────────

export async function listSavedSearches(): Promise<SavedSearchListResponse> {
  try {
    const response = await api.get<SavedSearchListResponse>('/v1/licenses/searches')
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function createSavedSearch(name: string, search_params: LicenseSearchParams): Promise<SavedSearch> {
  try {
    const response = await api.post<SavedSearch>('/v1/licenses/searches', { name, search_params })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function deleteSavedSearch(id: number): Promise<void> {
  try {
    await api.delete(`/v1/licenses/searches/${id}`)
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function toggleSavedSearchWatch(id: number, watch_enabled: boolean): Promise<SavedSearch> {
  try {
    const response = await api.patch<SavedSearch>(`/v1/licenses/searches/${id}/watch`, { watch_enabled })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/**
 * Include or exclude a saved search from lead auto-discovery.
 *
 * Independent of the watch toggle: watching emails the owner when matches change,
 * feeding leads shows the matches on the team's Leads page.
 */
export async function toggleSavedSearchFeedsLeads(id: number, feeds_leads: boolean): Promise<SavedSearch> {
  try {
    const response = await api.patch<SavedSearch>(`/v1/licenses/searches/${id}/feeds-leads`, { feeds_leads })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function getWatchSummary(): Promise<WatchSummaryResponse> {
  try {
    const response = await api.get<WatchSummaryResponse>('/v1/licenses/searches/watch-summary')
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function getNewMatches(id: number): Promise<WatchMatchesResponse> {
  try {
    const response = await api.get<WatchMatchesResponse>(`/v1/licenses/searches/${id}/new-matches`)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

// ── License Change Log ────────────────────────────────────────────────────────

/**
 * Fetch change log entries (admin only).
 * Pass reviewed=false to get only unreviewed entries.
 * page_size=1 is enough to get the total count for badge display.
 */
export async function getChangeLog(params?: {
  search?: string
  reviewed?: boolean
  change_type?: 'entity_name' | 'frn' | 'both'
  /** Two-letter code. Matches the **licensee's** state, not the filing agent's. */
  state?: string
  detected_within_days?: number
  sort?: ChangeLogSortKey
  order?: 'asc' | 'desc'
  page?: number
  page_size?: number
}): Promise<FCCLicenseChangeLogListResponse> {
  try {
    const response = await api.get<FCCLicenseChangeLogListResponse>(
      '/v1/licenses/change-log',
      { params },
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/**
 * Review (or un-review) many change-log entries in one request (admin only).
 *
 * Two modes. `ids` is an explicit selection, capped at 250 by the API. `filter`
 * sends the criteria and the `max_id` the list endpoint reported for that same
 * search — the server re-runs the filters and bounds the update to rows that existed
 * when the search ran, so an import arriving in between can't be swept up.
 *
 * Sending neither or both is a 422: the API refuses to guess at the scope of a write.
 */
export async function bulkReviewChangeLog(body: ChangeLogBulkReviewRequest): Promise<BulkResult> {
  try {
    const response = await api.post<BulkResult>('/v1/licenses/change-log/bulk-review', body)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/**
 * Mark a single change log entry reviewed, or clear that mark (admin only).
 *
 * Defaults to `true` so existing call sites keep their meaning.
 */
export async function markChangeLogReviewed(id: number, reviewed = true): Promise<void> {
  try {
    await api.patch(`/v1/licenses/change-log/${id}/reviewed`, { reviewed })
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/**
 * List licenses with an open construction obligation that is approaching or overdue.
 * Pass page_size=1 (defaults below) to read the approaching/overdue counts for a badge.
 */
export async function getConstructionDeadlines(params?: {
  within_days?: number
  include_overdue?: boolean
  overdue_within_days?: number
  linked_only?: boolean
  include_acknowledged?: boolean
  /** Free text over call sign and the displayed entity name. */
  search?: string
  /** Two-letter code. Matches the **licensee's** state, not the filing agent's. */
  state?: string
  contact?: ContactFilter
  page?: number
  page_size?: number
  sort?: string
  order?: 'asc' | 'desc'
}): Promise<ConstructionDeadlineListResponse> {
  try {
    const response = await api.get<ConstructionDeadlineListResponse>(
      '/v1/licenses/construction-deadlines',
      { params },
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/**
 * Acknowledge (or clear) a construction-deadline alert by license USI.
 */
/**
 * Acknowledge (or un-acknowledge) many construction alerts at once.
 *
 * Filter mode replays the same builder the list endpoint uses, so the set acted on
 * is the set displayed. No `max_id` here: targets are USIs rather than ids, and
 * acknowledging is fully reversible.
 */
export async function bulkAcknowledgeConstructionDeadlines(
  body: ConstructionBulkAcknowledgeRequest,
): Promise<BulkResult> {
  try {
    const response = await api.post<BulkResult>(
      '/v1/licenses/construction-deadlines/bulk-acknowledge',
      body,
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function acknowledgeConstructionDeadline(
  usi: number,
  acknowledged: boolean,
): Promise<ConstructionAlertState> {
  try {
    const response = await api.patch<ConstructionAlertState>(
      `/v1/licenses/construction-deadlines/${usi}/acknowledge`,
      { acknowledged },
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/** Record (or clear) a contact with the licensee about its construction deadline. */
export async function logConstructionContact(
  usi: number,
  contacted: boolean,
  method: ContactMethod = 'call',
): Promise<{ fcc_unique_system_identifier: number; contacted_at: string | null; contact_method: string | null }> {
  try {
    const response = await api.patch(
      `/v1/licenses/construction-deadlines/${usi}/contact`,
      { contacted, method },
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/** Client/lead deadlines coming up that nobody has contacted yet (the nav badge). */
export async function getConstructionNeedsContactCount(): Promise<number> {
  try {
    const response = await api.get<{ count: number }>(
      '/v1/licenses/construction-deadlines/needs-contact-count',
    )
    return response.data.count
  } catch {
    return 0
  }
}

/**
 * Get the Form 601 Schedule K construction-notification worksheet for a call sign.
 */
export async function getConstructionWorksheet(callSign: string): Promise<ConstructionWorksheet> {
  try {
    const response = await api.get<ConstructionWorksheet>(
      `/v1/licenses/${callSign}/construction-worksheet`,
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/**
 * Record (or clear) that we filed the 601 construction notification for a call sign.
 */
export async function markConstructionFiled(
  callSign: string,
  filed: boolean,
  filingReference?: string,
): Promise<ConstructionFilingResponse> {
  try {
    const response = await api.patch<ConstructionFilingResponse>(
      `/v1/licenses/${callSign}/construction-worksheet/filed`,
      { filed, filing_reference: filingReference || null },
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}
